import React from 'react';
import { useNavigate } from 'react-router-dom';

function LegalDisclosure() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="mb-8 flex items-center gap-2 text-purple-600 hover:text-purple-700 font-medium"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Home
        </button>

        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Legal Disclosure</h1>
          <p className="text-gray-600">Last Updated: January 25, 2025</p>
        </div>

        {/* Content */}
        <div className="prose prose-lg max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Company Information</h2>
            <div className="bg-gray-100 p-6 rounded-lg mb-4">
              <p className="text-gray-700"><strong>Legal Entity:</strong> ZuperBooks, LLC</p>
              <p className="text-gray-700"><strong>Business Type:</strong> Limited Liability Company</p>
              <p className="text-gray-700"><strong>State of Formation:</strong> Michigan, United States</p>
              <p className="text-gray-700"><strong>Headquarters:</strong> Michigan, United States</p>
              <p className="text-gray-700"><strong>Email:</strong> legal@zuperbooks.com</p>
              <p className="text-gray-700"><strong>Phone:</strong> 1-800-ZUPER-BK</p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Business Purpose</h2>
            <p className="text-gray-700 mb-4">
              ZuperBooks provides cloud-based inventory management and financial analytics software specifically designed for Direct-to-Consumer (DTC) brands. Our platform helps businesses track inventory, calculate costs, and make data-driven decisions.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Service Disclaimer</h2>
            <p className="text-gray-700 mb-4">
              <strong>No Financial Advice:</strong> ZuperBooks provides software tools and analytics but does not provide financial, accounting, legal, or tax advice. Users should consult with qualified professionals for such matters.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>No Guarantee of Results:</strong> While our platform provides data and recommendations, we cannot guarantee specific business outcomes, profit increases, or cost savings.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>User Responsibility:</strong> Users are solely responsible for their business decisions and the accuracy of data entered into the platform.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Data Accuracy</h2>
            <p className="text-gray-700 mb-4">
              ZuperBooks processes and displays data provided by users and integrated third-party services. While we strive for accuracy, we cannot guarantee the completeness or accuracy of:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>User-entered inventory data</li>
              <li>Third-party API data (e.g., sales channels)</li>
              <li>Calculated metrics and recommendations</li>
              <li>Historical reports and analytics</li>
            </ul>
            <p className="text-gray-700 mb-4">
              Users should regularly verify data accuracy and maintain backup records.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Third-Party Services</h2>
            <p className="text-gray-700 mb-4">
              ZuperBooks integrates with third-party services including:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li><strong>Authentication:</strong> Google OAuth for user login</li>
              <li><strong>Infrastructure:</strong> Cloud hosting providers (AWS, MongoDB Atlas)</li>
              <li><strong>Payment Processing:</strong> Stripe (when applicable)</li>
              <li><strong>Analytics:</strong> Google Analytics</li>
            </ul>
            <p className="text-gray-700 mb-4">
              We are not responsible for the availability, accuracy, or policies of these third-party services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Service Availability</h2>
            <p className="text-gray-700 mb-4">
              <strong>Uptime:</strong> We strive for 99.9% uptime but do not guarantee uninterrupted service. Maintenance windows will be communicated in advance when possible.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>Data Backup:</strong> We perform regular backups but recommend users maintain their own records of critical business data.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Intellectual Property Notice</h2>
            <p className="text-gray-700 mb-4">
              "ZuperBooks" and associated logos are trademarks of ZuperBooks, LLC. Unauthorized use is prohibited.
            </p>
            <p className="text-gray-700 mb-4">
              All software, content, and materials on the platform are protected by copyright and other intellectual property laws.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Regulatory Compliance</h2>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li><strong>GDPR:</strong> We comply with EU General Data Protection Regulation for European users</li>
              <li><strong>CCPA/CPRA:</strong> We comply with California privacy laws</li>
              <li><strong>SOC 2:</strong> Security and privacy controls are audited annually</li>
              <li><strong>Michigan Law:</strong> Our operations comply with Michigan state business regulations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Insurance and Liability</h2>
            <p className="text-gray-700 mb-4">
              ZuperBooks maintains appropriate business insurance including:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>General liability insurance</li>
              <li>Professional liability (E&O) insurance</li>
              <li>Cyber liability insurance</li>
            </ul>
            <p className="text-gray-700 mb-4">
              Liability limitations are specified in our Terms of Use.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Dispute Resolution</h2>
            <p className="text-gray-700 mb-4">
              <strong>Informal Resolution:</strong> We encourage users to contact us directly to resolve any disputes.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>Arbitration:</strong> Disputes that cannot be resolved informally may be subject to binding arbitration in Michigan.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>Class Action Waiver:</strong> Users agree to resolve disputes individually, not as part of a class action.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Export Compliance</h2>
            <p className="text-gray-700 mb-4">
              Our software is subject to U.S. export control laws. Users agree not to export or re-export the software to prohibited countries or entities.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Accessibility</h2>
            <p className="text-gray-700 mb-4">
              We are committed to making our platform accessible to users with disabilities. We strive to comply with WCAG 2.1 Level AA standards.
            </p>
            <p className="text-gray-700 mb-4">
              For accessibility concerns, contact: accessibility@zuperbooks.com
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Changes to This Disclosure</h2>
            <p className="text-gray-700 mb-4">
              We may update this Legal Disclosure from time to time. Material changes will be communicated via email or platform notification.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Contact Information</h2>
            <div className="bg-gray-100 p-6 rounded-lg">
              <p className="text-gray-700"><strong>ZuperBooks, LLC</strong></p>
              <p className="text-gray-700">Headquarters: Michigan, United States</p>
              <p className="text-gray-700">General Inquiries: info@zuperbooks.com</p>
              <p className="text-gray-700">Legal Matters: legal@zuperbooks.com</p>
              <p className="text-gray-700">Privacy Concerns: privacy@zuperbooks.com</p>
              <p className="text-gray-700">Support: support@zuperbooks.com</p>
              <p className="text-gray-700">Phone: 1-800-ZUPER-BK</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default LegalDisclosure;